import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertTriangle, XCircle, Shield, Activity, TrendingUp, Lock } from 'lucide-react';

/**
 * MaturityDashboardPreview — A polished glass mock-up showing what users
 * will see inside the platform. Mirrors the Hybrid Cyber-Audit palette:
 *  emerald = pass, amber = review, red = risk.
 */

const StatusPill = ({ status, count, label }) => {
  const styles = {
    pass: {
      color: 'text-emerald-300',
      bg: 'bg-emerald-400/10',
      border: 'border-emerald-400/30',
      icon: CheckCircle2,
    },
    review: {
      color: 'text-amber-300',
      bg: 'bg-amber-400/10',
      border: 'border-amber-400/30',
      icon: AlertTriangle,
    },
    risk: {
      color: 'text-red-400',
      bg: 'bg-red-400/10',
      border: 'border-red-400/30',
      icon: XCircle,
    },
  }[status];
  const Icon = styles.icon;
  return (
    <div className={`rounded-md p-4 border ${styles.bg} ${styles.border}`} data-testid={`preview-status-${status}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`h-4 w-4 ${styles.color}`} />
        <span className={`font-mono text-[10px] uppercase tracking-widest ${styles.color}`}>
          {label}
        </span>
      </div>
      <div className={`font-heading font-bold text-2xl ${styles.color}`}>{count}</div>
    </div>
  );
};

const ControlRow = ({ family, score, total }) => {
  const percentage = Math.round((score / total) * 100);
  const tone =
    percentage >= 75 ? 'emerald' : percentage >= 50 ? 'amber' : 'red';
  const colors = {
    emerald: { text: 'text-emerald-300', bar: 'bg-emerald-400' },
    amber: { text: 'text-amber-300', bar: 'bg-amber-400' },
    red: { text: 'text-red-400', bar: 'bg-red-400' },
  }[tone];

  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="font-mono text-[11px] uppercase tracking-widest text-slate-400 w-32 truncate">
        {family}
      </div>
      <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className={`h-full rounded-full ${colors.bar}`}
          style={{ width: `${percentage}%`, transition: 'width 1.2s ease-out' }}
        />
      </div>
      <div className={`font-mono text-xs font-semibold ${colors.text} w-12 text-right`}>
        {percentage}%
      </div>
    </div>
  );
};

const MaturityDashboardPreview = () => {
  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#07111f] py-24 sm:py-32" data-testid="maturity-preview-section">
      <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.22) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          {/* Left pitch */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
            data-testid="maturity-preview-copy"
          >
            <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-emerald-300 bg-white/5 border border-emerald-400/25 px-4 py-2 rounded-full mb-5 backdrop-blur-sm">
              <Activity className="h-3.5 w-3.5" />
              Live Maturity Dashboard
            </span>
            <h2 className="font-heading font-semibold text-4xl text-white mb-5 leading-tight">
              See your compliance posture <span className="text-emerald-300">at a glance</span>
            </h2>
            <p className="font-body text-lg text-slate-400 leading-relaxed mb-6">
              Every assessment gets distilled into a real-time dashboard with control-family scores,
              audit-ready visualizations, and traffic-light status across all 93 Annex&nbsp;A controls.
            </p>
            <div className="space-y-3">
              {[
                { icon: TrendingUp, text: 'Weighted maturity score with category breakdown' },
                { icon: Shield, text: 'Heatmap of all 93 ISO 27001:2022 Annex A controls' },
                { icon: Lock, text: 'Audit-ready PDF & Excel reports in one click' },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-400/10 border border-emerald-400/30">
                      <Icon className="h-4 w-4 text-emerald-300" />
                    </div>
                    <span className="text-slate-300 text-sm">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Dashboard mock-up */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-3"
            data-testid="maturity-preview-dashboard"
          >
            <div
              className="relative rounded-xl p-6 md:p-8"
              style={{
                background: 'rgba(15, 29, 47, 0.72)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(16px)',
                boxShadow: '0 25px 60px -10px rgba(0,0,0,0.5), 0 0 80px -20px rgba(52,211,153,0.15)',
              }}
            >
              {/* Window chrome */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  /assessment-summary
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">Live</span>
                </div>
              </div>

              {/* Overall score + stats row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                {/* Overall maturity gauge */}
                <div
                  className="rounded-md p-5"
                  style={{
                    background: 'rgba(52,211,153,0.06)',
                    border: '1px solid rgba(52,211,153,0.2)',
                  }}
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mb-2">
                    Overall Maturity
                  </div>
                  <div className="flex items-end gap-2 mb-3">
                    <span className="font-heading font-bold text-5xl text-white">72</span>
                    <span className="text-emerald-300 text-lg pb-1">%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-2">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: '72%',
                        background: 'linear-gradient(90deg,#f87171 0%,#fbbf24 50%,#34d399 100%)',
                      }}
                    />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-300">
                    Level 4 · Managed
                  </div>
                </div>

                {/* Status pills */}
                <div className="grid grid-cols-3 gap-2.5">
                  <StatusPill status="pass" count="68" label="Pass" />
                  <StatusPill status="review" count="19" label="Review" />
                  <StatusPill status="risk" count="6" label="Risk" />
                </div>
              </div>

              {/* Control-family breakdown */}
              <div
                className="rounded-md p-5"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    Annex A Control Families
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    93 controls
                  </span>
                </div>
                <ControlRow family="Organizational" score={28} total={37} />
                <ControlRow family="People" score={6} total={8} />
                <ControlRow family="Physical" score={8} total={14} />
                <ControlRow family="Technological" score={20} total={34} />
              </div>

              {/* Tag row */}
              <div className="flex items-center gap-2 mt-5 flex-wrap">
                <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-400/10 border border-emerald-400/30 px-2.5 py-1 rounded-full">
                  ISO 27001:2022
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-amber-300 bg-amber-400/10 border border-amber-400/30 px-2.5 py-1 rounded-full">
                  6 gaps flagged
                </span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                  Auditor-ready
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default MaturityDashboardPreview;
